// WebSocketProvider.js (Frontend Consumer)
import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Context } from '.';
import { Auth, API } from 'aws-amplify';
import AWS from 'aws-sdk';

const WebSocketContext = createContext(null);

const createNotificationAudio = () => {
  const audio = new Audio('/notification-18-270129.mp3');
  audio.preload = 'auto';
  return audio;
};

export const WebSocketProvider = ({ children }) => {
  const sqsRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const pollingRef = useRef(false);
  const messageListeners = useRef(new Set());
  const connectionAttemptsRef = useRef(0);
  const notificationSound = useRef(null);
  const processedMessagesRef = useRef(new Set());
  const contextDataRef = useRef({});
  const { userData, auth } = useContext(Context);
  
  // Stage-dependent queue URL
  const QUEUE_URL = process.env.REACT_APP_STAGE === 'prod' 
    ? 'https://sqs.us-east-1.amazonaws.com/922184331501/order-updates-queue' 
    : 'https://sqs.us-east-2.amazonaws.com/922184331501/order-updates-queue';
  
  // Initialize notification sound
  useEffect(() => {
    notificationSound.current = createNotificationAudio();
    
    notificationSound.current.addEventListener('error', (e) => {
      console.warn('Notification sound error:', e);
      if (notificationSound.current.src.includes('/notification-18-270129.mp3')) {
        notificationSound.current.src = '/notification-18-270129.mp3';
      }
    });
    
    return () => {
      if (notificationSound.current) {
        notificationSound.current.pause();
        notificationSound.current = null;
      }
    };
  }, []);
  
  // Keep context data updated
  useEffect(() => {
    contextDataRef.current = {
      institution: userData?.institution,
      cognitoId: userData?.cognitoId,
      userType: userData?.userType
    };
  }, [userData]);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    try {
      if (notificationSound.current) {
        notificationSound.current.currentTime = 0;
        const playPromise = notificationSound.current.play();
        
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.warn('Error playing notification:', error);
            if (error.name === 'NotSupportedError') {
              notificationSound.current.src = '/notification-18-270129.mp3';
              notificationSound.current.load();
            }
          });
        }
      }
    } catch (error) {
      console.error('Error playing notification sound:', error);
    }
  }, []);

  // Subscribe to messages
  const subscribe = useCallback((listener) => {
    messageListeners.current.add(listener);
    return () => messageListeners.current.delete(listener);
  }, []);

  // Notify all listeners
  const notifyListeners = useCallback((message) => {
    playNotificationSound();
    
    messageListeners.current.forEach(listener => {
      try {
        listener(message);
      } catch (error) {
        console.error('Error in message listener:', error);
      }
    });
  }, [playNotificationSound]);

  // Create updated message for multi-admin scenarios
  const createProcessedMessage = useCallback(async (sqs, originalMessage, updatedData) => {
    try {
      const sendResult = await sqs.sendMessage({
        QueueUrl: QUEUE_URL,
        MessageBody: JSON.stringify(updatedData),
        DelaySeconds: 5,
        MessageAttributes: originalMessage.MessageAttributes || {}
      }).promise();
      
      await sqs.deleteMessage({
        QueueUrl: QUEUE_URL,
        ReceiptHandle: originalMessage.ReceiptHandle
      }).promise();
      
      return true;
    } catch (error) {
      console.error("Failed to create processed message:", error);
      return false;
    }
  }, [QUEUE_URL]);

  // Refresh AWS credentials
  const refreshCredentials = useCallback(async () => {
    try {
      const credentials = await Auth.currentCredentials();
      AWS.config.update({
        region: process.env.REACT_APP_STAGE === 'prod' ? 'us-east-1' : 'us-east-2',
        credentials: Auth.essentialCredentials(credentials)
      });
      
      // Re-create SQS client with new credentials
      sqsRef.current = new AWS.SQS();
      return credentials;
    } catch (error) {
      console.error("Error refreshing credentials:", error);
      return null;
    }
  }, []);

  // Poll messages from SQS queue
  const pollMessages = useCallback(async (sqs, currentData) => {
    try {
      const { Messages } = await sqs.receiveMessage({
        QueueUrl: QUEUE_URL,
        MaxNumberOfMessages: 10,
        WaitTimeSeconds: 20,
        VisibilityTimeout: 60,
        MessageAttributeNames: ['All']
      }).promise();
      
      if (!Messages || Messages.length === 0) return;
      
      for (const msg of Messages) {
        try {
          let messageData;
          try {
            messageData = JSON.parse(msg.Body);
          } catch (parseError) {
            console.error(`Failed to parse message ${msg.MessageId}:`, parseError);
            await sqs.deleteMessage({
              QueueUrl: QUEUE_URL,
              ReceiptHandle: msg.ReceiptHandle
            }).promise();
            continue;
          }
          
          // Skip messages for other institutions
          if (messageData.institution !== currentData.institution) {
            await sqs.changeMessageVisibility({
              QueueUrl: QUEUE_URL,
              ReceiptHandle: msg.ReceiptHandle,
              VisibilityTimeout: 1 // Make immediately available to other consumers
            }).promise();
            continue;
          }
          
          // Check if this message is for this admin
          const isForThisAdmin = messageData.cognitoIds && 
                               messageData.cognitoIds.includes(currentData.cognitoId);
          
          if (!isForThisAdmin) {
            await sqs.changeMessageVisibility({
              QueueUrl: QUEUE_URL,
              ReceiptHandle: msg.ReceiptHandle,
              VisibilityTimeout: 1 // Make immediately available to other admins
            }).promise();
            continue;
          }
          
          // Create a unique message key for this admin+message combination
          const messageId = messageData.messageId || msg.MessageId;
          const messageKey = `${messageId}-${currentData.cognitoId}`;
          
          // Check if we've already processed this message
          if (processedMessagesRef.current.has(messageKey)) {
            await sqs.deleteMessage({
              QueueUrl: QUEUE_URL,
              ReceiptHandle: msg.ReceiptHandle
            }).promise();
            continue;
          }
          
          // Check if message has timed out
          const timeoutPeriod = 30 * 60 * 1000; // 30 minutes
          const hasTimedOut = messageData.firstProcessedTimestamp && 
                             (Date.now() - messageData.firstProcessedTimestamp > timeoutPeriod);
          
          if (hasTimedOut) {
            await sqs.deleteMessage({
              QueueUrl: QUEUE_URL,
              ReceiptHandle: msg.ReceiptHandle
            }).promise();
            continue;
          }
          
          // Notify all listeners about this message
          notifyListeners(messageData);
          processedMessagesRef.current.add(messageKey);
          
          // Handle multi-admin message processing
          const isMultiAdminMessage = messageData.cognitoIds && messageData.cognitoIds.length > 1;
          
          if (isMultiAdminMessage) {
            const updatedMessageData = { ...messageData };
            
            if (!updatedMessageData.processedBy) {
              updatedMessageData.processedBy = [];
            }
            
            if (!updatedMessageData.firstProcessedTimestamp) {
              updatedMessageData.firstProcessedTimestamp = Date.now();
            }
            
            if (!updatedMessageData.processedBy.includes(currentData.cognitoId)) {
              updatedMessageData.processedBy.push(currentData.cognitoId);
            }
            
            // Check if all admins have processed this message
            const allAdminsProcessed = messageData.cognitoIds.every(id => 
              updatedMessageData.processedBy.includes(id)
            );
            
            if (allAdminsProcessed) {
              // All admins have seen this message, delete it
              await sqs.deleteMessage({
                QueueUrl: QUEUE_URL,
                ReceiptHandle: msg.ReceiptHandle
              }).promise();
            } else {
              // Filter out admins who have already processed this message
              const remainingAdmins = messageData.cognitoIds.filter(id => 
                !updatedMessageData.processedBy.includes(id)
              );
              
              // Create new message for remaining admins
              const newMessageData = {
                ...updatedMessageData,
                cognitoIds: remainingAdmins,
                originalCognitoIds: messageData.originalCognitoIds || messageData.cognitoIds
              };
              
              // Create processed message and delete the original
              await createProcessedMessage(sqs, msg, newMessageData);
            }
          } else {
            // Single admin message, delete after processing
            await sqs.deleteMessage({
              QueueUrl: QUEUE_URL,
              ReceiptHandle: msg.ReceiptHandle
            }).promise();
          }
        } catch (messageError) {
          console.error(`Error processing message ${msg.MessageId}:`, messageError);
          try {
            // Delete problematic messages to avoid infinite processing attempts
            await sqs.deleteMessage({
              QueueUrl: QUEUE_URL,
              ReceiptHandle: msg.ReceiptHandle
            }).promise();
          } catch (deleteError) {
            console.error(`Failed to delete problematic message ${msg.MessageId}:`, deleteError);
          }
        }
      }
    } catch (error) {
      console.error("SQS polling error:", error);
      throw error; // Re-throw to trigger reconnection logic
    }
  }, [QUEUE_URL, createProcessedMessage, notifyListeners]);

  // Main connection and polling function
  const connectAndPoll = useCallback(async () => {
    const { institution, cognitoId, userType } = contextDataRef.current;
    
    // Check user data validity
    if (userType === undefined && auth === true) {
      try {
        // Make sure we have institution data to make the API call
        if (institution) {
          const userData = await API.get(
            'main',
            `/user/get-userdata/${institution}`,
            {}
          );
          
          // Update the context data ref with the fetched data
          contextDataRef.current = {
            ...contextDataRef.current,
            userType: userData.userType,
            cognitoId: userData.cognitoId || contextDataRef.current.cognitoId
          };
          
          // Check if user is admin with the updated data
          if (contextDataRef.current.userType !== 'admin') {
            console.log("User is not admin (from API data), skipping SQS setup");
            return;
          }
        } else {
          console.log("Institution undefined, cannot fetch user data");
          return;
        }
      } catch (error) {
        console.error("Error fetching user data from API:", error);
        return;
      }
    } else if (userType !== 'admin') {
      console.log("User is not admin, skipping SQS setup");
      return;
    }
    
    if (!institution || !cognitoId) {
      console.log("Missing institution or cognitoId, skipping SQS setup");
      return;
    }
    
    if (pollingRef.current) {
      console.log("Already polling, skipping new connection");
      return;
    }
    
    try {
      // Refresh credentials before starting
      await refreshCredentials();
      
      setIsConnected(true);
      pollingRef.current = true;
      connectionAttemptsRef.current = 0;
      
      console.log(`Starting SQS polling for institution: ${institution}, admin: ${cognitoId}`);
      
      // Main polling loop
      while (pollingRef.current) {
        try {
          const currentData = contextDataRef.current;
          
          // Check if user is still admin
          if (currentData.userType !== 'admin') {
            console.log("User no longer admin, stopping polling");
            pollingRef.current = false;
            setIsConnected(false);
            break;
          }
          
          await pollMessages(sqsRef.current, currentData);
        } catch (error) {
          console.error("Error during polling cycle:", error);
          
          if (error.code === 'ExpiredTokenException' || 
              error.message.includes('expired') || 
              error.code === 'CredentialsError') {
            console.log("Token expired, refreshing credentials...");
            await refreshCredentials();
          }
          
          // Brief pause before next polling attempt
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
      
      console.log("SQS polling ended");
      setIsConnected(false);
    } catch (error) {
      console.error("Error setting up SQS connection:", error);
      setIsConnected(false);
      pollingRef.current = false;
      
      // Implement exponential backoff for reconnection
      const maxBackoff = 5 * 60 * 1000; // 5 minutes max
      const reconnectDelay = Math.min(
        maxBackoff, 
        1000 * Math.pow(2, Math.min(10, connectionAttemptsRef.current))
      );
      
      connectionAttemptsRef.current++;
      console.log(`Will retry connection in ${reconnectDelay/1000} seconds (attempt ${connectionAttemptsRef.current})`);
      
      setTimeout(() => {
        console.log("Attempting to reconnect to SQS...");
        connectAndPoll();
      }, reconnectDelay);
    }
  }, [auth, pollMessages, refreshCredentials]);

  // Set up SQS connection and credential refresh
  useEffect(() => {
    let isMounted = true;
    let credentialRefreshTimer = null;
    
    const setupCredentialRefresh = async () => {
      try {
        const credentials = await refreshCredentials();
        
        if (!isMounted || !credentials) return;
        
        // Set timer for next refresh (slightly before expiry)
        if (credentials.expireTime) {
          const expirationTime = credentials.expireTime;
          const now = new Date();
          // Refresh 5 minutes before expiration
          const timeUntilExpiry = expirationTime - now - (5 * 60 * 1000);
          
          if (credentialRefreshTimer) {
            clearTimeout(credentialRefreshTimer);
          }
          
          console.log(`Scheduling credential refresh in ${Math.floor(timeUntilExpiry / 60000)} minutes`);
          credentialRefreshTimer = setTimeout(setupCredentialRefresh, 
            timeUntilExpiry > 0 ? timeUntilExpiry : 30000);
        } else {
          // If no expireTime is available, refresh every 45 minutes
          if (credentialRefreshTimer) {
            clearTimeout(credentialRefreshTimer);
          }
          credentialRefreshTimer = setTimeout(setupCredentialRefresh, 45 * 60 * 1000);
        }
      } catch (error) {
        console.error("Error in credential refresh:", error);
        
        if (isMounted) {
          credentialRefreshTimer = setTimeout(setupCredentialRefresh, 30000);
        }
      }
    };
    
    setupCredentialRefresh();
    connectAndPoll();
    
    // Periodic cleanup of processed messages cache
    const cleanupInterval = setInterval(() => {
      if (processedMessagesRef.current.size > 500) {
        console.log("Cleaning up processed message cache");
        const oldSize = processedMessagesRef.current.size;
        processedMessagesRef.current = new Set(
          Array.from(processedMessagesRef.current).slice(-200)
        );
        console.log(`Reduced cache from ${oldSize} to ${processedMessagesRef.current.size} entries`);
      }
    }, 10 * 60 * 1000); // Clean up every 10 minutes
    
    return () => {
      console.log("WebSocketProvider cleanup - stopping polling");
      isMounted = false;
      pollingRef.current = false;
      setIsConnected(false);
      
      if (credentialRefreshTimer) {
        clearTimeout(credentialRefreshTimer);
      }
      
      if (cleanupInterval) {
        clearInterval(cleanupInterval);
      }
    };
  }, [connectAndPoll, refreshCredentials]);

  // Context value to expose to consumers
  const value = {
    isConnected,
    subscribe,
    playNotificationSound,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

// Hook to consume WebSocket context
export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};